// GoodEnglish.js
// Good English 通用课件引擎
import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import StarIcon from '@mui/icons-material/Star';
import GoodEnglishPage from './GoodEnglishPage';
import RewardEffect from '../phonics/RewardEffect';
import '../phonics/Phonics.css';

/**
 * GoodEnglish 引擎组件
 *
 * props:
 *   data: {
 *     id, title, mediaBase, audio, pages: [{ image, text, start, end }]
 *   }
 */
function GoodEnglish({ data }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [rewardTrigger, setRewardTrigger] = useState(0);
  const audioRef = useRef(null);

  // 设置浏览器页签标题
  useEffect(() => {
    document.title = data.title;
    return () => { document.title = '17Talk'; };
  }, [data.title]);

  const totalPages = data.pages.length;
  const isFirst = currentPage === 0;
  const isLast = currentPage === totalPages - 1;

  const goNext = () => setCurrentPage((s) => Math.min(s + 1, totalPages - 1));
  const goPrev = () => setCurrentPage((s) => Math.max(s - 1, 0));

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  // 页面变化时停止音频
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [currentPage]);

  return (
    <div className="phonics-root" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* 奖励效果 */}
      <RewardEffect
        key={rewardTrigger}
        trigger={rewardTrigger > 0}
        onDone={() => setRewardTrigger(0)}
      />

      {/* 内容区 */}
      <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        <GoodEnglishPage
          key={currentPage}
          page={data.pages[currentPage]}
          audioSrc={data.audio}
          onAudioRef={(ref) => { audioRef.current = ref; }}
        />
      </Box>

      {/* 底部导航 */}
      <div className="phonics-footer" style={{ flexShrink: 0 }}>
        <Tooltip title="上一页">
          <IconButton
            size="small"
            onClick={goPrev}
            disabled={isFirst}
            className="phonics-nav-btn"
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <div className="phonics-progress">
          {data.pages.map((_, idx) => (
            <React.Fragment key={idx}>
              <div
                className={'phonics-progress-dot' + (idx === currentPage ? ' active' : '')}
                title={`Page ${idx + 1}`}
                onClick={() => setCurrentPage(idx)}
                style={{ cursor: 'pointer' }}
              />
              {idx < totalPages - 1 && <div className="phonics-progress-line" />}
            </React.Fragment>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Tooltip title="太棒了！">
            <IconButton
              size="small"
              onClick={() => setRewardTrigger((t) => t + 1)}
              className="phonics-nav-btn"
              sx={{ color: '#FFD700' }}
            >
              <StarIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
            <IconButton
              size="small"
              onClick={toggleFullscreen}
              className="phonics-nav-btn"
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="下一页">
            <IconButton
              size="small"
              onClick={goNext}
              disabled={isLast}
              className="phonics-nav-btn"
            >
              <ArrowForwardIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

export default GoodEnglish;
